
import ErrorBoundary from '../../components/ErrorBoundary'
import {
    ContactPhoneOutlined as ContactPhoneOutlinedIcon, AddBoxOutlined as AddBoxOutlinedIcon,
    EditOutlined as EditOutlinedIcon,
    HistoryOutlined as HistoryOutlinedIcon,
    Delete as DeleteIcon, Email as EmailIcon,
    MultipleStop as MultipleStopIcon, VoiceOverOffRounded as VoiceOverOffRoundedIcon,
    SyncDisabled as SyncDisabledIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material"

import {
    DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
    SCOUT_ACCOUNT_FILTER_OPTIONS,
    formatPhoneNumber,
    listScoutAccountsPage,
    getEnterprise,
    getUsersEmails,
    createAccount,
    updateAccount,
} from "../../services/scout"
import { isAccountBlocked } from "../../services/utils"
import React, { useCallback, useEffect, useReducer, useState } from "react"
import lockedReducer from "../../lib/reducer"
import { useHomeAccountsPolling } from "../../components/home/useHomeAccountsPolling"
import { MenuItem, TextField, Tooltip } from "@mui/material"
import Table from "../../components/ui/Table"
import AddNumber from "./modals/addnumber"
import { emitSnackbar } from "../../lib/snackbar-emitter"
import manageErrors from "../../lib/error-management"
import ConnectPhone from "./modals/connectphone"
import DeletePhone from "./modals/removephone"
import DisconnectPhone from "./modals/disconnectphone"
import { loader } from "../../lib/loader"
import ResendEmail from "./modals/resendemail"
import ConnectHistory from "./modals/connecthistory"
import ExclusionList from "./modals/exclusionlist"
import Supervisor from "./modals/supervisor"
import { renderExceptions, userHasGroup } from "../../services/utils"

import {
    getMaxLicenses
} from "../../services/utils"


const status_dict = {
    'open': (<span style={{ color: 'green' }}>Conectado</span>),
    'connecting': (<span style={{ color: 'orange' }}>Conectando</span>),
    'close': (<span style={{ color: 'red' }}>Desconectado</span>),
    'export': (<span style={{ color: 'blue' }}>Exportando</span>)
}

const normalizePhoneStatus = (value = '') => String(value || '').trim().toLowerCase()

const resolvePhoneStatus = (row = {}) => {
    const connectionState = normalizePhoneStatus(row.connection_state)
    const status = normalizePhoneStatus(row.status)

    if (connectionState === 'export' || status === 'export') return 'export'
    if (status === 'open') return 'open'

    return 'close'
}

const possibleActions = ({ row, setState, blocked, enterprise }) => {
    const status = resolvePhoneStatus(row)

    return Object.entries({
        connect: { icon: <MultipleStopIcon />, title: 'Conectar', show: !blocked && !['open', 'export'].includes(status), hide: true, onClick: () => setState({ connect: row }) },
        disconnect: { icon: <SyncDisabledIcon />, title: 'Desconectar', show: !blocked && status === 'open', hide: true, onClick: () => setState({ disconnect: row }) },
        resendmail: { icon: <EmailIcon />, title: 'Reenviar e-mail', show: !blocked && !['open', 'export'].includes(status), onClick: () => setState({ resend: row }) },
        edit: { icon: <EditOutlinedIcon />, title: 'Editar', show: !blocked && !['export'].includes(status), onClick: () => setState({ edit: row }) },
        historic: { icon: <HistoryOutlinedIcon />, title: 'Histórico', show: !blocked && !['export'].includes(status), onClick: () => setState({ historic: row }) },
        excludedlist: { icon: <VoiceOverOffRoundedIcon />, title: 'Lista de exclusão', show: !blocked && renderExceptions(enterprise) && status === 'open', onClick: () => setState({ excluded: row }) },
        supervisor: { icon: <AdminPanelSettingsIcon />, title: "Alterar gestor", show: true && userHasGroup(["administrador"]), onClick: () => setState({ supervisor: row }) },
        delete: { icon: <DeleteIcon />, title: 'Excluir', show: !blocked && !['open', 'export'].includes(status) && userHasGroup(["administrador"]), onClick: () => setState({ delete: row }) }
    })
}

const phoneManagementConfig = {}

const initialState = {
    rows: [],
    users: [],
    used_licenses: 0,
    total_licenses: 0,
    loading: false,
    loading_more: false,
    page: 1,
    has_more: false,
    create: null,
    edit: null,
    connect: null,
    disconnect: null,
    resend: null,
    historic: null,
    excluded: null,
    delete: null,
    supervisor: null
}

const mergeRowsById = (currentRows = [], nextRows = [], reset = false) => {
    const mergedRows = reset ? [] : [...currentRows]
    const rowIndexes = new Map(mergedRows.map((row, index) => [String(row.id), index]))

    nextRows.forEach((row) => {
        const rowId = String(row?.id || '')
        if (!rowId) return

        const normalizedRow = {...row, status: resolvePhoneStatus(row)}

        if (rowIndexes.has(rowId)) {
            mergedRows[rowIndexes.get(rowId)] = {
                ...mergedRows[rowIndexes.get(rowId)],
                ...normalizedRow,
            }
            return
        }

        rowIndexes.set(rowId, mergedRows.length)
        mergedRows.push(normalizedRow)
    })

    return mergedRows
}

const Phones = () => {

    const [enterprise, setEnterprise] = useState(null)
    const [state, setState] = useReducer(lockedReducer, initialState)
    const [accountFilterBy, setAccountFilterBy] = useState('name')
    const [appliedAccountFilterBy, setAppliedAccountFilterBy] = useState('name')
    const [accountFilterText, setAccountFilterText] = useState('')
    const [debouncedAccountFilterText, setDebouncedAccountFilterText] = useState('')

    const availableLicenses = state.total_licenses - state.used_licenses
    const isBlocked = isAccountBlocked(enterprise)

    useEffect(() => {
        const debounceTimer = window.setTimeout(() => {
            setDebouncedAccountFilterText(accountFilterText.trim())
            setAppliedAccountFilterBy(accountFilterBy)
        }, 350)

        return () => window.clearTimeout(debounceTimer)
    }, [accountFilterText])

    const effectiveAccountFilterBy = debouncedAccountFilterText ? appliedAccountFilterBy : 'name'

    const loadAccountsPage = async ({ page = 1, reset = false, showLoader = false } = {}) => {
        if (showLoader) setState({ loading: true })
        else setState({ loading_more: true })

        const scoutaccounts = await listScoutAccountsPage({
            page,
            pageSize: DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
            filterBy: effectiveAccountFilterBy,
            filter: debouncedAccountFilterText,
        })

        setState({
            loading: false,
            loading_more: false,
            page: scoutaccounts.page,
            has_more: scoutaccounts.hasMore,
            used_licenses: scoutaccounts.count,
            rows: mergeRowsById(reset ? [] : state.rows, scoutaccounts.results, reset),
        })

        return scoutaccounts
    }

    const initialize = useCallback(async () => {

        setState({ loading: true })
        loader.on()

        const [enterpriseData, users, scoutaccounts] = await Promise.all([
            getEnterprise(),
            getUsersEmails(),
            listScoutAccountsPage({
                page: 1,
                pageSize: DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
                filterBy: effectiveAccountFilterBy,
                filter: debouncedAccountFilterText,
            })
        ])

        setEnterprise(enterpriseData)

        setState({
            ...initialState,
            total_licenses: getMaxLicenses(enterpriseData),
            used_licenses: scoutaccounts?.count || 0,
            users: users || [],
            page: scoutaccounts?.page || 1,
            has_more: Boolean(scoutaccounts?.hasMore),
            rows: mergeRowsById([], scoutaccounts?.results || [], true),
        })
        loader.off()
    }, [debouncedAccountFilterText, effectiveAccountFilterBy])

    const subCreateAccount = async (data) => {
        setState({ loading: true })
        const response = await createAccount(data)
        if (response.status === 201) {
            emitSnackbar({ message: 'Conta criada com sucesso!', severity: 'success' })
            await initialize()
        } else {
            emitSnackbar({ message: manageErrors({ error: response.data }), severity: "warning" })
        }

        setState({ loading: false })
    }

    const subUpdateAccount = async (data) => {
        setState({ loading: true })
        const response = await updateAccount(state.edit, data)
        if (response.status === 200) {
            emitSnackbar({ message: 'Número atualizado com sucesso!', severity: 'success' })
            setState({ edit: null })
            await initialize()
        } else {
            emitSnackbar({ message: manageErrors({ error: response.data }), severity: "warning" })
        }

        setState({ loading: false })
    }

    useEffect(() => {
        initialize()
    }, [initialize])

    useHomeAccountsPolling({
        accounts: state.rows,
        accountsLoading: state.loading,
        onRefresh: (nextAccounts) => setState({ rows: mergeRowsById(state.rows, nextAccounts, false) }),
        fetchAccounts: async () => {
            const data = await listScoutAccountsPage({
                page: 1,
                pageSize: Math.max(state.rows.length, DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE),
                filterBy: effectiveAccountFilterBy,
                filter: debouncedAccountFilterText,
            })
            return data.results
        },
        interval: 10000,
    })

    const handleRowsScroll = (event) => {
        const element = event.currentTarget
        const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight)

        if (distanceFromBottom > 160 || state.loading || state.loading_more || !state.has_more) {
            return
        }

        loadAccountsPage({ page: state.page + 1, reset: false }).catch(() => {
            emitSnackbar({ message: 'Não foi possível carregar mais números.', severity: 'error' })
            setState({ loading_more: false })
        })
    }

    return (
        <>
            {!isBlocked && state.create &&
                <AddNumber users={state.users} data={state.create} handleClose={() => setState({ create: null })} submit={(subCreateAccount)} />
            }
            {!isBlocked && state.edit &&
                <AddNumber users={state.users} data={state.edit} handleClose={() => setState({ edit: null })} submit={(subUpdateAccount)} />
            }
            {!isBlocked && state.connect &&
                <ConnectPhone account={state.connect} handleClose={() => setState({ connect: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.disconnect &&
                <DisconnectPhone
                    account={state.disconnect}
                    handleClose={() => setState({ disconnect: null })}
                    handleSuccess={() => {
                        const disconnectedId = state.disconnect.id
                        setState({
                            disconnect: null,
                            rows: state.rows.map((row) =>
                                String(row.id) === String(disconnectedId)
                                    ? { ...row, status: 'close', connection_state: 'close' }
                                    : row
                            ),
                        })
                    }}
                />
            }
            {!isBlocked && state.resend &&
                <ResendEmail account={state.resend} handleClose={() => setState({ resend: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.delete &&
                <DeletePhone account={state.delete} handleClose={() => setState({ delete: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.historic &&
                <ConnectHistory account={state.historic} handleClose={() => setState({ historic: null })} />
            }
            {!isBlocked && state.excluded &&
                <ExclusionList account={state.excluded} handleClose={() => setState({ excluded: null })} handleSuccess={initialize} />
            }
            {!isBlocked && state.supervisor &&
                <Supervisor account={state.supervisor} users={state.users} handleClose={() => setState({ supervisor: null })} handleSuccess={initialize} />
            }
            <section className="scout-settings-section">
                <div className="scout-settings-section__label">
                    <ContactPhoneOutlinedIcon />
                    <span>{phoneManagementConfig?.sectionLabel || 'Números monitorados'}: {state.used_licenses} de {state.total_licenses} licenças utilizadas</span>
                </div>

                <div className="scout-account-filter-controls scout-account-filter-controls--settings">
                    <TextField
                        select
                        size="small"
                        label="Filtro"
                        className="scout-account-filter-control"
                        value={accountFilterBy}
                        InputLabelProps={{ shrink: true }}
                        onChange={({ target }) => setAccountFilterBy(target.value)}
                    >
                        {SCOUT_ACCOUNT_FILTER_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        label="Buscar"
                        className="scout-account-filter-control"
                        placeholder={accountFilterBy === 'phone_number' ? 'Buscar por número' : 'Buscar por nome'}
                        value={accountFilterText}
                        InputLabelProps={{ shrink: true }}
                        onChange={({ target }) => setAccountFilterText(target.value)}
                    />
                </div>

                {!state.loading && (
                    <>
                        <div onScroll={handleRowsScroll} style={{ maxHeight: 'min(68vh, 46rem)', overflowY: 'auto' }}>
                            <Table
                                columns={[
                                    { id: 'name', label: 'Nome', render: (row) => row.name },
                                    { id: 'phone', label: 'Telefone', render: (row) => formatPhoneNumber(row.phone_number) },
                                    { id: 'status', label: 'Status', render: (row) => status_dict[resolvePhoneStatus(row)] },
                                    { id: "create_by_name", label: "Gestor", render: (row) => row.created_by_name },
                                    {
                                        id: 'actions',
                                        label: '',
                                        align: 'right',
                                        render: (row) => (
                                            <div className="scout-settings-table__actions">
                                                {possibleActions({ row, setState, blocked: isBlocked, enterprise: enterprise }).map(([action, data]) => (
                                                    <React.Fragment key={`${row.id}-${action}`}>
                                                        {!(!data.show && data.hide) && (
                                                            <Tooltip title={data.show ? data.title : "Indisponível"}>
                                                                <span>
                                                                    <button
                                                                        onClick={data.onClick}
                                                                        disabled={!data.show}
                                                                        type="button"
                                                                        className={`scout-settings-icon-action ${data.show ? '' : 'disabled'}`}>
                                                                        {data.icon}
                                                                    </button>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ),
                                    },
                                ]}
                                data={state.rows}
                                rowKey={(row) => row.id}
                                emptyMessage="Nenhum número monitorado encontrado"
                                className="scout-settings-card--table"
                                tableClassName="scout-settings-table--phones"
                            />
                            {state.loading_more ? (
                                <div className="scout-settings-table__row scout-settings-table__row--loading">Carregando...</div>
                            ) : null}
                        </div>

                        {userHasGroup(["administrador"]) &&
                            <button
                                onClick={() => !isBlocked && setState({ create: { id: 0 } })}
                                type="button"
                                className="scout-settings-add-row"
                                disabled={isBlocked}
                            >
                                <AddBoxOutlinedIcon />
                                <span>{phoneManagementConfig?.addLabel || 'Adicionar'} <span style={{ fontWeight: 'lighter' }}>({availableLicenses} disponíveis)</span></span>
                            </button>
                        }
                    </>
                )}

                {state.loading ? <p className="scout-inline-feedback">Atualizando telefones...</p> : null}
                {/* {accountsError ? <p className="scout-inline-feedback">{accountsError}</p> : null} */}
            </section>
        </>
    )
}

const PhonesWithBoundary = (props) => (
    <ErrorBoundary
        componentName="Phones"
        showReload
        logToBackend
        title="Erro na tela de conexões"
        message="Ocorreu um erro ao carregar ou gerenciar os números de telefone. Tente novamente."
    >
        <Phones {...props} />
    </ErrorBoundary>
)

export default PhonesWithBoundary
